import { z } from "zod";

/// Shared HTTP boundary helper for the webui.
///
/// Every feature's `*.api.ts` calls into this module. The helper:
///   0. Resolves the request URL against `VITE_API_BASE_URL`. Empty (the dev
///      default) keeps paths relative so the Vite proxy forwards them to the
///      shore; in production the bundle is built with the shore's public
///      origin so the SPA can call the API on a different host.
///   1. Sets `credentials: "include"` and standard JSON headers.
///   2. Parses JSON bodies safely (tolerates non-JSON 5xx).
///   3. On a non-2xx response: extracts the shore's `{ error: { code,
///      message, details? } }` envelope and throws `ApiError` with that
///      shape. Falls back to `code: "UNKNOWN"` if the body isn't an error
///      envelope.
///   4. On a 204: returns `undefined` (callers should pass `z.unknown()`).
///   5. On a 2xx with body: Zod-parses the `.data` payload through the
///      caller-supplied schema (T3 / FE-SEC6, validate every response,
///      even from our own backend). Parsing is lenient-with-logging: a
///      benign shape drift (backend omits a nullable field, ships a new
///      field, stale deploy) is reported via `reportResponseDrift` and the
///      raw payload is returned instead of throwing, so a 200 never crashes
///      the UI or rolls back an optimistic update. The happy path still
///      validates and normalizes (defaults/transforms applied).
///      Financial review reads can opt into `strictResponse` and fail closed.
///
/// Previously duplicated across identity / people / todos / comments / bulk
/// feature APIs; consolidated when the fourth consumer landed.

/**
 * HTTP error class, captures status + machine-readable error code so the
 * UI can branch on (e.g. 401 → redirect to /login) without re-parsing.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details: unknown;
  constructor(input: { status: number; code: string; message: string; details?: unknown }) {
    super(input.message);
    this.name = "ApiError";
    this.status = input.status;
    this.code = input.code;
    this.details = input.details ?? null;
  }
}

/**
 * Best-effort HTTP status from an unknown thrown error. Handles `ApiError`
 * (which carries `status`) and any other object with a numeric `status`
 * field. Callers branch on it (e.g. hide a section on 404 rather than toast).
 */
export function errorStatus(error: unknown): number | undefined {
  return error && typeof error === "object" && "status" in error
    ? (error as { status?: number }).status
    : undefined;
}

/**
 * Public origin of the shore API. Trailing slash trimmed so joining a
 * leading-slash path never doubles the separator. Empty string (dev default)
 * leaves callers' relative paths untouched for the Vite proxy to forward.
 */
// reason: import.meta.env is undefined in Node unit tests (node:test + tsx);
//         guard so ApiError can be imported in tests without crashing.
const API_BASE_URL = (import.meta.env?.VITE_API_BASE_URL ?? "").replace(/\/+$/, "");

/** Join the configured base origin to a caller-supplied path (`/auth/me`). */
export function resolveApiUrl(path: string): string {
  return API_BASE_URL ? `${API_BASE_URL}${path}` : path;
}

const envelopeSchema = z.object({
  // reason: void-returning shore endpoints (PATCH/DELETE/reorder) send
  // { data: undefined, meta }, which serializes to just { meta } (JSON drops
  // undefined). Zod 4 made z.unknown() a required key, so a plain z.unknown()
  // here throws on those successful 200s and surfaces as a false "save failed".
  // .optional() restores the pre-Zod-4 contract: a 200 must never crash the UI.
  data: z.unknown().optional(),
  meta: z.object({ requestId: z.string() }).partial().optional(),
});

const errorEnvelopeSchema = z.object({
  error: z.object({
    code: z.string(),
    message: z.string(),
    details: z.unknown().optional(),
  }),
  meta: z.object({ requestId: z.string() }).partial().optional(),
});

export async function apiCall<T>(
  url: string,
  init: RequestInit,
  responseSchema: z.ZodTypeAny,
  options: { strictResponse?: boolean } = {},
): Promise<T> {
  const headers = new Headers(init.headers);
  headers.set("accept", "application/json");
  if (init.body instanceof FormData) {
    // reason: FormData must set its own multipart boundary; keep the shared
    // `accept` + credentialed fetch behavior without forcing JSON headers.
    headers.delete("content-type");
  } else if (init.body == null) {
    headers.delete("content-type");
  } else {
    headers.set("content-type", "application/json");
  }

  const res = await fetch(resolveApiUrl(url), {
    credentials: "include",
    // reason: these are dynamic, auth-scoped responses. The shore sets no
    // Cache-Control, so without this the browser memory-caches credentialed
    // GETs (e.g. /auth/me) and replays a previous session's body after sign-in
    //, the app then fires tenant-scoped requests with a stale workspace id
    // (404s) until a full reload clears the memory cache.
    cache: "no-store",
    headers,
    ...init,
  });

  let bodyRaw: unknown = null;
  const ct = res.headers.get("content-type") ?? "";
  if (ct.includes("application/json")) {
    try {
      bodyRaw = await res.json();
    } catch {
      bodyRaw = null;
    }
  }

  if (!res.ok) {
    const parsed = errorEnvelopeSchema.safeParse(bodyRaw);
    if (parsed.success) {
      throw new ApiError({
        status: res.status,
        code: parsed.data.error.code,
        // Shore sends a friendly message for SERVICE_UNAVAILABLE (pool
        // saturation under cron-tick bursts), but if it ever doesn't we
        // override the wire message so users see something actionable
        // instead of a generic "HTTP 503" string.
        message:
          parsed.data.error.code === "SERVICE_UNAVAILABLE"
            ? parsed.data.error.message || "The service is busy. Try again in a few seconds."
            : parsed.data.error.message,
        details: parsed.data.error.details ?? null,
      });
    }
    // Non-envelope 503 (proxy, load balancer, cold start). Map to the same
    // friendly text so the UI doesn't have to special-case status codes.
    if (res.status === 503) {
      throw new ApiError({
        status: 503,
        code: "SERVICE_UNAVAILABLE",
        message: "The service is busy. Try again in a few seconds.",
      });
    }
    throw new ApiError({
      status: res.status,
      code: "UNKNOWN",
      message: `HTTP ${res.status}`,
    });
  }

  if (res.status === 204) {
    return undefined as T;
  }

  const env = envelopeSchema.parse(bodyRaw);
  const parsed = responseSchema.safeParse(env.data);
  if (parsed.success) {
    return parsed.data as T;
  }
  // A 2xx body diverged from the caller's schema (backend omitted a nullable
  // field, shipped a new field, stale deploy, etc.). Per FE-SEC6 we still
  // validate every response, but a benign drift must not crash the UI: a
  // thrown ZodError here rejects the caller's promise, which for optimistic
  // mutations rolls back a correct value and surfaces a generic error toast.
  // Report the drift for observability and fall back to the raw payload so the
  // request resolves. The success path above keeps full validation + defaults.
  reportResponseDrift(url, parsed.error);
  if (options.strictResponse) {
    throw new ApiError({
      status: 502,
      code: "INVALID_RESPONSE",
      message: "This data could not be verified. Refresh and try again.",
    });
  }
  return env.data as T;
}

/**
 * Logs a 2xx response whose shape drifted from the caller's schema. DEV-only
 * console signal for now (single seam to forward to telemetry later). Never
 * user-facing and never throws.
 */
function reportResponseDrift(url: string, error: z.ZodError): void {
  if (import.meta.env?.DEV) {
    console.warn(
      `[apiCall] response schema drift for ${url}:`,
      error.issues.map((issue) => `${issue.path.join(".") || "(root)"}: ${issue.message}`),
    );
  }
}
